import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLazyCommand, useApiErrors } from '@soap/modules';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { lessonLoadingStateKey } from '../InProgressUnitsPanel/loadingStateKeys';
import { confirmActionButtonClass } from '../../constants/brandPalette';
import { cn } from '../../lib/utils';

const LESSON_RATING_MIN = 1;
const LESSON_RATING_MAX = 10;
const LESSON_RATINGS = Array.from(
    { length: LESSON_RATING_MAX - LESSON_RATING_MIN + 1 },
    (_, i) => LESSON_RATING_MIN + i
);

interface LessonDetailDto {
    e251_LessonId?: string;
    e251_LessonTitle?: string;
}

interface LessonCompletionDialogProps {
    unitId: string;
    lessonId: string;
    lesson: LessonDetailDto;
    onClose: () => void;
    onSuccess?: () => void;
    setLoadingStates?: React.Dispatch<React.SetStateAction<Set<string>>>;
    onApplyOptimistic?: () => void;
    onRevertOptimistic?: () => void;
}

export function LessonCompletionDialog({
    unitId,
    lessonId,
    lesson,
    onClose,
    onSuccess,
    setLoadingStates,
    onApplyOptimistic,
    onRevertOptimistic
}: LessonCompletionDialogProps) {
    const [lessonRating, setLessonRating] = useState<number | null>(null);
    const [whatCouldBeImproved, setWhatCouldBeImproved] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [sendFinishLessonCommand] = useLazyCommand('C271v1_FinishLesson');

    const lessonLoadingKey = lessonLoadingStateKey(unitId, lessonId);
    const clearLessonLoading = React.useCallback(() => {
        setLoadingStates?.(prev => {
            const next = new Set(prev);
            next.delete(lessonLoadingKey);
            return next;
        });
    }, [lessonLoadingKey, setLoadingStates]);

    useApiErrors(() => {
        setIsSubmitting(false);
        clearLessonLoading();
        onRevertOptimistic?.();
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting || lessonRating === null) return;

        setIsSubmitting(true);
        onApplyOptimistic?.();
        setLoadingStates?.(prev => new Set(prev).add(lessonLoadingKey));

        try {
            const trimmedWhatCouldBeImproved = whatCouldBeImproved?.trim() || null;

            await sendFinishLessonCommand({
                C271_UnitId: unitId,
                C271_LessonId: lessonId,
                C271_HowDidLessonGo: String(lessonRating),
                C271_WhatCouldBeImproved: trimmedWhatCouldBeImproved
            });

            setIsSubmitting(false);

            if (onSuccess) {
                onSuccess();
            } else {
                onClose();
            }
        } catch (error) {
            console.error('Error finishing lesson:', error);
            setIsSubmitting(false);
            clearLessonLoading();
            onRevertOptimistic?.();
        }
    };

    const handleCancel = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 pointer-events-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 overflow-hidden">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Complete Lesson
                        </h2>
                        <button
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <p className="text-sm text-gray-600 mb-6">
                        Please provide feedback about this lesson:{' '}
                        <strong>{lesson.e251_LessonTitle || 'Untitled Lesson'}</strong>
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                How did the lesson go?
                            </Label>
                            <p className="text-xs text-gray-500 mb-3">
                                Rate from 1 (poor) to 10 (excellent).
                            </p>
                            <div
                                className="grid grid-cols-5 sm:grid-cols-10 gap-2"
                                role="group"
                                aria-label="Lesson rating from 1 to 10"
                            >
                                {LESSON_RATINGS.map(rating => {
                                    const isSelected = lessonRating === rating;
                                    return (
                                        <button
                                            key={rating}
                                            type="button"
                                            onClick={() => setLessonRating(rating)}
                                            disabled={isSubmitting}
                                            aria-pressed={isSelected}
                                            className={cn(
                                                'h-10 rounded-md border text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-action-blue',
                                                isSelected
                                                    ? 'border-confirm-action bg-confirm-action-tint text-confirm-action-dark'
                                                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                            )}
                                        >
                                            {rating}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="whatCouldBeImproved" className="text-sm font-medium text-gray-700 mb-2 block">
                                What could be improved?{' '}
                                <span className="font-normal text-gray-500">(optional)</span>
                            </Label>
                            <Textarea
                                id="whatCouldBeImproved"
                                value={whatCouldBeImproved}
                                onChange={e => setWhatCouldBeImproved(e.target.value)}
                                placeholder="Share any suggestions for improvement..."
                                rows={4}
                                className="w-full"
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting || lessonRating === null}
                                className={confirmActionButtonClass}
                            >
                                {isSubmitting ? 'Completing...' : 'Complete Lesson'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
